import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';
import { readWho } from './utils/contactContext';
import { getTrafficContext } from './utils/trafficContext';
import TurnstileWidget from './components/TurnstileWidget';

// Cloudflare Turnstile site key — widget covers physioactivity.com,
// qa.physioactivity.com and localhost. Server-side half is
// TURNSTILE_SECRET_KEY, set directly in Azure (see api/submit-contact).
const TURNSTILE_SITE_KEY = '0x4AAAAAAFMDc8QxLaIuGiLL';

/*
 * /msk-cost — door 4 entry and the one lead-capture form on the site.
 *
 * The estimate is ungated so it stays visible for door 2 findability;
 * the report is gated, because that is a fair exchange and it is where
 * the qualification happens. The form captures headcount, sector and
 * the door, per the board: the door is part of the lead definition, so
 * it has to arrive with the lead rather than be inferred later.
 *
 * Coefficients are published UK figures, every one sourced on the page.
 * Where two sources disagree we use the average and say so. The cost is
 * deliberately the conservative direct figure with presenteeism shown
 * separately, because a number a CFO can check is worth more than a
 * bigger one they can dismantle.
 *
 * Nothing here computes a saving. It is the buyer's arithmetic on their
 * own headcount, which is the safe form under the claim chain.
 */

const MSK_DAYS_PER_EMP = 0.80;   // ONS + Versus Arthritis, averaged
const DAYS_PER_CASE = 14.0;      // ONS 2024, matches HSE 2024/25
const WORK_DAYS = 260;
const ONCOST = 1.18;             // employer NI + pension
const PRESENTEEISM = 2.0;        // CIPD / Deloitte model 2-3x absence; conservative end
const DEFAULT_SALARY = 39039;    // ONS ASHE, April 2025

const fmt = (n) => Math.round(n).toLocaleString('en-GB');

const MskCostPage = () => {
    const [headcount, setHeadcount] = useState(1000);
    const [salary, setSalary] = useState(DEFAULT_SALARY);
    const { search, pathname } = useLocation();
    /* Read once, on mount. If this were recomputed from `search` it would fall
       back to the default the moment the param is stripped below, silently
       undoing the prefill. React Router does not observe replaceState today, so
       it happens to survive — but relying on that is a trap for whoever changes
       the router next. */
    const [who, setWho] = useState(() => readWho(search));

    /* The CALCULATOR is the employer tool and does not move: employees, fifty
       upwards, workforce language throughout. Only the form adapts, because a
       physiotherapist has clients and a researcher has participants, and neither
       starts with fifty of them.

       Which means the two stop being the same number. For an employer the form
       field still drives the calculator, as it always has. For a physiotherapist
       or researcher it is their own count, held separately, so typing 3 clients
       cannot rewrite a workforce calculation into nonsense. */
    const UNIT = { physio: 'Clients', researcher: 'Participants' };
    const unit = UNIT[who] || 'Employees';
    const ownCount = who === 'physio' || who === 'researcher';
    const [clients, setClients] = useState(10);
    const count = ownCount ? clients : headcount;
    const setCount = ownCount
        ? (v) => setClients(Math.min(10000, Math.max(1, v || 1)))
        : (v) => setHeadcount(Math.min(10000, Math.max(50, v || 50)));

    /* Strip ?who= once it has been read. It has done its job by the time the
       field is set, and leaving it on means prefilled variants of /contact get
       shared and indexed as separate URLs — which matters more than usual while
       the canonical tag is still hardcoded to the homepage (G1). */
    useEffect(() => {
        if (search && /[?&]who=/i.test(search) && window.history.replaceState) {
            window.history.replaceState(null, '', pathname);
        }
    }, [search, pathname]);

    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    // Bumped on a failed submit to remount TurnstileWidget — tokens are
    // single-use, and this is simpler than an imperative reset API for the
    // one form that needs it.
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);

    const days = headcount * MSK_DAYS_PER_EMP;
    const affected = days / DAYS_PER_CASE;
    const absence = days * ((salary || DEFAULT_SALARY) / WORK_DAYS * ONCOST);
    const presenteeism = absence * PRESENTEEISM;
    const total = absence + presenteeism;


    /* Posts to /api/submit-contact, which relays to the Power Automate email
       flow. It has to go through our own API: the flow's trigger URL carries a
       SAS signature, and anything the browser calls ends up in the public
       bundle. The estimate travels with the enquiry so the reply can start from
       their number rather than asking for it again. */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        /* No gate on turnstileToken here. An ad-blocker killing Cloudflare's
           challenge script is a common, real failure mode, not an edge case —
           see api/submit-contact's handling of an empty token. Submitting
           without one just means this layer has nothing to say either way. */
        setError('');
        setSending(true);

        const form = e.target;
        const val = (id) => (form.querySelector('#' + id) || {}).value || '';

        try {
            const res = await fetch('/api/submit-contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: val('pa-email'),
                    name: val('pa-name'),
                    organisation: val('pa-org'),
                    who: val('pa-who'),
                    sector: val('pa-sector'),
                    door: val('pa-door'),
                    headcount: count,
                    countLabel: unit,
                    /* Only send the estimate to people the calculator was written for.
                       A physiotherapist's client count paired with a workforce cost
                       would read as "Clients: 40, Total MSK Health cost: £425,225",
                       which is two unrelated numbers next to each other. */
                    totalCost: ownCount ? '' : '£' + fmt(total),
                    perEmployee: ownCount ? '' : '£' + fmt(total / (headcount || 1)),
                    notes: val('pa-notes'),
                    consent: !!(form.querySelector('#pa-consent') || {}).checked,
                    website: val('pa-website'),
                    turnstileToken,
                    ...getTrafficContext(),
                }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.error || 'Something went wrong');
            }
            /* Event name unchanged on purpose: renaming it would break continuity
               with whatever GA4 has already recorded. */
            firePAEvent('roi_brief_download', { headcount });
            firePAEvent('contact_submit', { who });
            setSent(true);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
            // Turnstile tokens are single-use — force a fresh widget for the retry.
            setTurnstileToken('');
            setTurnstileResetKey((k) => k + 1);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="pa2 pa-cost">
            <section className="hero" id="top">
                <div className="wrap">
                    <h1 className="on-dark">
                        <span className="setup">What is MSK Health</span>
                        <span className="payoff">costing you?</span>
                    </h1>
                    <p className="lede">
                        See how your organisation compares, using published national data on what
                        musculoskeletal (MSK) health costs in the workplace. Enter your total
                        headcount to get started.
                    </p>
                    {/* Data currency, not a page review date. The review date lives in
                        the footer, once, for the whole site. This says how old the
                        published figures are, which a reader checking our arithmetic
                        needs to know. */}
                    <p className="dateline">
                        This is where you are starting from, on figures current as of August 2026
                        and updated when ONS and HSE republish.
                    </p>

                    <div className="calcgrid">
                        <div className="calc">
                            <div className="ctl">
                                <div className="big">
                                    <label htmlFor="pa-hc">Employees</label>
                                    <div className="v">{fmt(headcount)}</div>
                                    <input
                                        id="pa-hc"
                                        type="range"
                                        min="50"
                                        max="10000"
                                        step="50"
                                        value={headcount}
                                        onChange={(e) => setHeadcount(+e.target.value)}
                                        aria-label="Number of employees"
                                    />
                                    <div className="range-ends"><span>50</span><span>10,000</span></div>
                                </div>
                                <div className="sal">
                                    <label htmlFor="pa-sal">Average salary <em>edit to use yours</em></label>
                                    <div className="money">
                                        <span>&pound;</span>
                                        <input
                                            id="pa-sal"
                                            type="number"
                                            min="15000"
                                            step="500"
                                            value={salary}
                                            onChange={(e) => setSalary(+e.target.value)}
                                        />
                                    </div>
                                    <p className="hint">UK median, ONS 2025</p>
                                </div>
                            </div>

                            <div className="out" aria-live="polite">
                                <div className="o"><div className="n">{fmt(affected)}</div><div className="l">employees likely to be absent with an MSK Health problem in a year</div></div>
                                <div className="o"><div className="n">{fmt(days)}</div><div className="l">working days lost to MSK Health across your workforce</div></div>
                            </div>

                            <div className="costs" aria-live="polite">
                                <div className="c"><span className="k">Absenteeism</span><span className="v">&pound;{fmt(absence)}</span></div>
                                <div className="c"><span className="k">Presenteeism <em>modelled at 2&times;</em></span><span className="v">&pound;{fmt(presenteeism)}</span></div>
                                <div className="c tot"><span className="k">Total cost of MSK Health, per year</span><span className="v">&pound;{fmt(total)}</span></div>
                            </div>

                            <div className="perhead">
                                <span className="k">Per employee, per year</span>
                                <span className="v">&pound;{fmt(total / (headcount || 1))}</span>
                            </div>
                            <p className="nextstep">
                                Fill in the form to get your custom MSK Health impact report
                            </p>

                            <p className="cavt">
                                Workforce average, not adjusted for sector. The report accounts for yours.{' '}
                                <a href="#sources">Method and sources<span className="ast">*</span></a>
                            </p>
                        </div>

                        {sent ? (
                        <div id="contact" className="pa-form pa-sent">
                            <h3>Thank you. That has reached us.</h3>
                            <p className="fl">
                                We reply to every enquiry. Your
                                figures came with it, so we will pick up from your number rather
                                than asking for it again.
                            </p>
                            <p className="fnote alt">
                                Meanwhile, the method and every source behind your estimate are{' '}
                                <a href="#sources">on this page</a>.
                            </p>
                        </div>
                        ) : (
                        <form id="contact" className="pa-form" onSubmit={handleSubmit}>
                            {/* Titled "Contact us" because the header CTA lands here. This is the
                                only form on the site, so it has to serve both the reader who came
                                for the calculator and the one who just wants to talk to someone. */}
                            <h3>Contact us</h3>
                            <p className="fl">Whether you are looking at this for your organisation or for yourself, this is the way to reach us. We reply to every enquiry.</p>

                            {/* Opens on whoever the reader was before they got here, from
                                ?who=. Still a plain select they can change: a prefill is a
                                good guess, not an answer, and the guess is wrong often
                                enough that it must never be read-only. */}
                            <div className="f">
                                <label htmlFor="pa-who">I am</label>
                                <select id="pa-who" value={who} onChange={(e) => setWho(e.target.value)}>
                                    <option value="employer">Looking at this for my organisation</option>
                                    <option value="individual">Asking for myself</option>
                                    <option value="physio">A physiotherapist or partner</option>
                                    <option value="researcher">A researcher</option>
                                    <option value="other">Something else</option>
                                </select>
                            </div>
                            <div className="f">
                                <label htmlFor="pa-door">What prompted you to look?</label>
                                <select id="pa-door" defaultValue="">
                                    <option value="">Select</option>
                                    <option value="1">We are already looking for MSK Health support</option>
                                    <option value="2">We are looking for a software or tech solution</option>
                                    <option value="3">We are building or refreshing our benefits package</option>
                                    <option value="4">We want to understand what MSK Health is costing us</option>
                                    <option value="5">Something else</option>
                                </select>
                            </div>
                            <div className="frow">
                                <div className="f"><label htmlFor="pa-name">Name</label><input id="pa-name" type="text" placeholder="Full name" /></div>
                                <div className="f"><label htmlFor="pa-org">Organisation (if you have one)</label><input id="pa-org" type="text" placeholder="Company name" /></div>
                            </div>
                            <div className="f">
                                <label htmlFor="pa-email">Business email (if you have one)</label>
                                <input id="pa-email" type="email" placeholder="you@yourcompany.com" required />
                            </div>
                            <div className="frow">
                                <div className="f">
                                    <label htmlFor="pa-fhc">{unit}</label>
                                    <input id="pa-fhc" type="number" min={ownCount ? 1 : 50} value={count}
                                        onChange={(e) => setCount(+e.target.value)} />
                                </div>
                                <div className="f">
                                    <label htmlFor="pa-sector">Sector</label>
                                    <select id="pa-sector" defaultValue="">
                                        <option value="">Select</option>
                                        <option>Construction</option>
                                        <option>Manufacturing</option>
                                        <option>Transport and logistics</option>
                                        <option>Healthcare</option>
                                        <option>Retail and hospitality</option>
                                        <option>Professional services</option>
                                        <option>Public sector</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Last, optional, and deliberately open. The dropdowns above
                                capture what we thought to ask; this is where someone tells
                                us the thing we did not. It is also the escape hatch for a
                                physiotherapist or researcher reading employer-worded
                                options above. */}
                            <div className="f">
                                <label htmlFor="pa-notes">Anything else? (optional)</label>
                                <textarea id="pa-notes" rows="3" maxLength="1200"
                                    placeholder="Background, a specific question, or what you are hoping to work out" />
                            </div>

                            <label className="consent">
                                <input id="pa-consent" type="checkbox" required />
                                <span>I agree to PhysioActivity holding these details to send the report and respond to this enquiry. <Link to="/privacy">Privacy notice</Link></span>
                            </label>

                            {/* Honeypot. Off-screen and not announced, so a person never
                                meets it and a bot fills it like any other field. */}
                            <input id="pa-website" name="website" type="text" tabIndex="-1"
                                autoComplete="off" aria-hidden="true" className="pa-hp" />

                            <TurnstileWidget
                                key={turnstileResetKey}
                                siteKey={TURNSTILE_SITE_KEY}
                                onToken={setTurnstileToken}
                            />

                            <button className="pa-submit" type="submit" disabled={sending}>
                                {sending ? 'Sending...' : 'Send'}
                            </button>
                            {error && <p className="formerr">{error}</p>}
                            <p className="fnote">No newsletter, and no sharing with anyone else.</p>
                            <p className="fnote alt">
                                Asking for yourself? <Link to="/individuals">Start here</Link>, and{' '}
                                <Link to="/individuals#employer">this</Link> tells you whether your
                                employer already covers it.
                            </p>
                        </form>
                        )}
                    </div>
                </div>
            </section>

            <section className="tint" id="sources">
                <div className="wrap">
                    <h2>
                        <span className="setup">Where the numbers come from.</span>
                        <span className="payoff">Every one of them.</span>
                    </h2>
                    <p className="lede">
                        Each coefficient, its value, and the published source it derives from.
                        Where two sources disagree the estimate uses the average rather than the
                        higher one, and says so.
                    </p>

                    {/* Coefficient names stay as "MSK" throughout this table. They label
                        published ONS, HSE and CIPD statistics, and those sources measure
                        MSK conditions — restating them as "MSK Health" would misquote
                        them. Everywhere the site speaks in its own voice it says MSK
                        Health; here it is quoting. */}
                    <div className="tablewrap">
                    <table className="ref">
                        <tbody>
                            <tr><th>Coefficient</th><th>Value</th><th>Source</th></tr>
                            <tr>
                                <td><b>Sickness days lost per worker per year</b></td><td>4.4</td>
                                <td><a href="https://www.ons.gov.uk/employmentandlabourmarket/peopleinwork/labourproductivity/articles/sicknessabsenceinthelabourmarket/2023and2024" target="_blank" rel="noopener noreferrer">ONS, Sickness absence in the UK labour market, 2024</a>. 148.9m days lost, 4.4 per worker. <b>The conservative of the two available figures.</b> The <a href="https://www.cipd.org/uk/knowledge/reports/health-well-being-work/" target="_blank" rel="noopener noreferrer">CIPD Health and Wellbeing at Work survey 2025</a> reports 9.4 days, more than double. ONS measures the labour force; CIPD surveys employers. We use ONS.</td>
                            </tr>
                            <tr>
                                <td><b>MSK share of sickness absence</b></td><td>17.75% <span style={{ color: '#6b7784' }}>(avg)</span></td>
                                <td>15.5% from ONS 2024. Around a fifth from <a href="https://www.arthritis-uk.org/media/flpbvm2m/arthritisuk_state_of_msk_health_-report_2025.pdf" target="_blank" rel="noopener noreferrer">Versus Arthritis, State of Musculoskeletal Health 2025</a>. The <a href="https://www.cipd.org/uk/knowledge/reports/health-well-being-work/" target="_blank" rel="noopener noreferrer">CIPD 2025 survey</a> separately finds 31% of organisations name musculoskeletal injuries among their top three causes of absence, and <a href="https://arma.uk.net/" target="_blank" rel="noopener noreferrer">ARMA</a>, the alliance of over 40 patient and professional bodies, puts MSK second only to minor illness as a cause of sickness absence.</td>
                            </tr>
                            <tr>
                                <td><b>MSK days lost per employee per year</b></td><td>0.80</td>
                                <td>Derived two ways and averaged: 4.4 &times; 17.75% = 0.78, and 28.2m MSK days divided by the UK workforce, about 0.83. For scale, <a href="https://www.nihr.ac.uk/innovation-musculoskeletal-health-oruk-msk" target="_blank" rel="noopener noreferrer">ORUK and NIHR</a> put total UK working days lost to MSK conditions at over 30 million a year.</td>
                            </tr>
                            <tr>
                                <td><b>Average length of an MSK absence</b></td><td>14.0 days</td>
                                <td>ONS 2024. Matches <a href="https://www.hse.gov.uk/statistics/causdis/msd.htm" target="_blank" rel="noopener noreferrer">HSE 2024/25</a>, which reports 7.1m days across 511,000 workers with work-related musculoskeletal disorders.</td>
                            </tr>
                            <tr>
                                <td><b>Presenteeism multiplier</b></td><td>2&times; <span style={{ color: '#6b7784' }}>(modelled)</span></td>
                                <td>Modelled rather than measured. The closest published UK work is <a href="https://www.deloitte.com/uk/en/services/consulting/analysis/mental-health-and-employers-the-case-for-investment.html" target="_blank" rel="noopener noreferrer">Deloitte&rsquo;s Mental health and employers</a>, which puts presenteeism at roughly &pound;24bn of a &pound;51bn total. Note that this covers mental health rather than MSK Health, so it establishes that presenteeism dominates without fixing a multiple for this condition. <a href="https://www.cipd.org/uk/knowledge/reports/health-well-being-work/" target="_blank" rel="noopener noreferrer">CIPD 2025</a> reports presenteeism rising but does not cost it. We apply 2&times;, the bottom of the range used in workplace-health modelling, so the total is best read as a range.</td>
                            </tr>
                            <tr>
                                <td><b>Median full-time salary</b> <span style={{ color: '#6b7784' }}>(default)</span></td><td>&pound;39,039</td>
                                <td><a href="https://www.ons.gov.uk/employmentandlabourmarket/peopleinwork/earningsandworkinghours/bulletins/annualsurveyofhoursandearnings/2025" target="_blank" rel="noopener noreferrer">ONS Annual Survey of Hours and Earnings, April 2025</a>. Editable, so use your own.</td>
                            </tr>
                            <tr>
                                <td><b>UK MSK prevalence</b> <span style={{ color: '#6b7784' }}>(context)</span></td><td>20.3m</td>
                                <td>Around a third of the UK population lives with an MSK condition, per <a href="https://www.nihr.ac.uk/innovation-musculoskeletal-health-oruk-msk" target="_blank" rel="noopener noreferrer">ORUK and NIHR</a>. Not used in the estimate. It is why the workforce figure above is not a niche number.</td>
                            </tr>
                            <tr>
                                <td><b>Employer good practice</b> <span style={{ color: '#6b7784' }}>(context)</span></td><td>n/a</td>
                                <td><a href="https://www.nice.org.uk/guidance/ng13" target="_blank" rel="noopener noreferrer">NICE NG13, Workplace health: management practices</a> is the national guideline on how employers should manage workplace health, including the physical demands of work. The reference point for what is expected of you, independent of any vendor.</td>
                            </tr>
                            <tr>
                                <td><b>The pathway your people wait in</b> <span style={{ color: '#6b7784' }}>(context)</span></td><td>n/a</td>
                                <td><a href="https://www.england.nhs.uk/elective-care/best-practice-solutions/musculoskeletal/" target="_blank" rel="noopener noreferrer">NHS England, Musculoskeletal health</a>. What an employee faces before anything an employer provides reaches them, and why waiting time is part of the cost.</td>
                            </tr>
                            <tr>
                                <td><b>Loaded daily cost</b></td><td>salary / 260 &times; 1.18</td>
                                <td>260 working days, plus 18% for employer National Insurance and pension.</td>
                            </tr>
                        </tbody>
                    </table>
                    </div>

                    <div className="faq two-up">
                        <div className="qa">
                            <h3>What counts as MSK Health?</h3>
                            <p>Musculoskeletal Health, usually shortened to MSK Health, covers muscles, joints, bones, tendons and the soft tissue around them. In an absence context it is the back pain, neck and shoulder problems, and knee and hip trouble that keep people off work or working below par. It is the second largest cause of sickness absence in the UK after minor illness.</p>
                        </div>
                        <div className="qa">
                            <h3>How is the MSK Health cost estimate calculated?</h3>
                            <p>It applies published UK figures to your headcount. Musculoskeletal conditions account for between 15.5 and 20 per cent of sickness absence depending on the source, averaging 17.75 per cent. Applied to 4.4 sickness days per worker per year, that gives roughly 0.8 MSK days per employee. Cost is that figure multiplied by a loaded daily employment cost derived from your average salary, with presenteeism added at the bottom of the modelled range.</p>
                        </div>
                        <div className="qa">
                            <h3>How much does MSK absence cost a UK employer?</h3>
                            <p>It depends on headcount and salary, which is what the calculator above is for. On median UK figures a 1,000-person organisation loses around 800 working days a year to MSK, costing roughly £140,000 in direct absence before presenteeism is counted. Your own figures will differ, and the method is shown so you can check it.</p>
                        </div>
                        <div className="qa">
                            <h3>Why does presenteeism matter more than absence?</h3>
                            <p>Absence is the part an employer can see and count. Presenteeism, people working through pain at reduced capacity, is not recorded anywhere, and published workplace-health modelling consistently puts it above the cost of absence. Note that it is modelled rather than measured, which is why it is shown separately rather than folded into one total.</p>
                        </div>
                        <div className="qa">
                            <h3>Is this an estimate or a quote?</h3>
                            <p>An estimate, built from public data and your headcount. It says nothing about what our service costs, and nothing about what it would save. We do not claim a saving. It is your arithmetic on your own workforce.</p>
                        </div>
                        <div className="qa">
                            <h3>Which sources are used, and how current are they?</h3>
                            <p>ONS sickness absence 2024, ONS earnings April 2025, HSE 2024/25, Versus Arthritis 2025 and CIPD 2025. Every one is linked above. The figures are reviewed each year when ONS and HSE republish, and the review date is in the footer.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="white pa-back">
                <div className="wrap">
                    <h2><span className="setup">You have the number.</span><span className="payoff">Take it with you.</span></h2>
                    <p className="lede">
                        The report writes your figures up with the method and every source, so it can be
                        forwarded to whoever holds the budget.
                    </p>
                    <a className="btn btn-t" href="#top">Get the report</a>
                    <p className="quiet">
                        Wondering what an employer can actually see, or who writes the programmes?{' '}
                        <Link to="/trust-centre">Trust centre</Link> and <Link to="/blog/who-writes-your-programme">who writes your programme</Link>.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default MskCostPage;
